import {
	BoxGeometry,
	BufferGeometry,
	Float32BufferAttribute,
	Group,
	Mesh,
	MeshBasicMaterial,
} from 'three';
import { textureLoader } from './instances.js';

export default class Settlement {
	block = 100;
	blocks_x = 5;
	blocks_z = this.blocks_x;
	road_w = 10;
	curb_h = 1;
	subdiv = 2;
	inner_block_margin = 5;
	build_max_h = 300;
	build_min_h = 10;
	block_h_dev = 10;
	build_exp = 5;
	groundColor = 0x666666;
	buildingColor = 0xe8e8e8;

	constructor() {
		this.width = this.block * this.blocks_x;
		this.length = this.block * this.blocks_z;
		this.buildings = [];
		this.mesh = new Group();
		this.curbs = new Group();
		this.mesh.add(this.curbs);
		this.heightmap = this.setupHeightmap();
		this.setupBlocks();
		if (this.buildings.length) {
			this.buildings = mergeMeshes(this.buildings);
			this.mesh.add(this.buildings);
		}
	}

	setupHeightmap() {
		const heightmap = [];
		const center = Math.floor(this.blocks_x / 2);
		for (let i = 0; i < this.blocks_x; i++) {
			for (let j = 0; j < this.blocks_z; j++) {
				if (!heightmap[i]) heightmap[i] = [];
				const x = i - center;
				const y = j - center;
				heightmap[i][j] = isPointInCircle({ x: 0, y: 0 }, center, { x, y })
					? (center - Math.abs(x) + center - Math.abs(y)) / (center * 2)
					: 0;
			}
		}
		heightmap[center][center] = 0;
		return heightmap;
	}

	setupBlocks() {
		for (let i = 0; i < this.blocks_x; i++) {
			for (let j = 0; j < this.blocks_z; j++) {
				const x = this.block * i + this.block / 2 - this.width / 2;
				const y = this.block * j + this.block / 2 - this.length / 2;
				const hm = this.heightmap[i][j];
				if (hm === 0) continue;
				const h = mapToRange(hm, this.build_min_h, this.build_max_h, this.build_exp);
				const w = this.block - this.road_w;
				const inner = w - this.inner_block_margin * 2;
				const curb = createBoxMesh(this.groundColor, w + this.road_w, w + this.road_w, this.curb_h);
				curb.position.set(x, y, this.curb_h / 2);
				this.curbs.add(curb);
				this.setupBuildings(x, y, inner, inner, h, this.subdiv, this.buildingColor);
			}
		}
	}

	setupBuildings(x, y, w, l, h, sub, color, buildings = []) {
		const depth = Math.pow(2, this.subdiv);
		const tall = Math.round((h / this.build_max_h) * 100) > 90;
		const sliceDeviation = 15;
		if (sub < 1 || tall) {
			const building = new Building({
				h: getRandInt(h - this.block_h_dev, h + this.block_h_dev),
				w,
				l,
				x,
				y,
				tall,
				color,
			});
			buildings.push(building.group);
			if (buildings.length >= depth || tall) this.buildings.push(...buildings);
			return;
		}

		const dir = w === l ? chance(50) : w > l;
		if (dir) {
			const offset = Math.abs(getRandInt(0, sliceDeviation));
			const between = this.inner_block_margin / 2;
			const half = w / 2;
			const xPrime = x + offset;
			const w1 = Math.abs(x + half - xPrime) - between;
			const w2 = Math.abs(x - half - xPrime) - between;
			this.setupBuildings(xPrime + w1 / 2 + between, y, w1, l, h, sub - 1, color, buildings);
			this.setupBuildings(xPrime - w2 / 2 - between, y, w2, l, h, sub - 1, color, buildings);
		} else {
			const offset = Math.abs(getRandInt(0, sliceDeviation));
			const between = this.inner_block_margin / 2;
			const half = l / 2;
			const yPrime = y + offset;
			const l1 = Math.abs(y + half - yPrime) - between;
			const l2 = Math.abs(y - half - yPrime) - between;
			this.setupBuildings(x, yPrime + l1 / 2 + between, w, l1, h, sub - 1, color, buildings);
			this.setupBuildings(x, yPrime - l2 / 2 - between, w, l2, h, sub - 1, color, buildings);
		}
	}
}

class Building {
	constructor(opts) {
		this.parts = [
			createBoxMeshWithOptions({
				color: 'wall',
				w: opts.w,
				l: opts.l,
				h: opts.h,
				x: opts.x,
				y: opts.y,
				z: opts.h / 2,
				shadow: true,
			}),
		];
		this.group = this.parts[0];
	}
}

function mapToRange(val, min, max, exp = 1) {
	const weighted = val >= 0.9 ? val : Math.pow(val, exp);
	return Math.floor(weighted * (max - min)) + min;
}

function mergeMeshes(meshes) {
	const geometries = [];
	for (const mesh of meshes) {
		mesh.updateMatrix();
		const geometry = mesh.geometry.clone();
		geometry.applyMatrix4(mesh.matrix);
		geometries.push(geometry);
	}
	const combined = mergeGeometries(geometries);
	return new Mesh(combined, new MeshBasicMaterial({ map: buildingTexture1 }));
}

function mergeGeometries(geometries) {
	const positions = [];
	const normals = [];
	const uvs = [];
	for (const source of geometries) {
		const geometry = source.index ? source.toNonIndexed() : source;
		const position = geometry.getAttribute('position');
		const normal = geometry.getAttribute('normal');
		const uv = geometry.getAttribute('uv');
		positions.push(...position.array);
		if (normal) normals.push(...normal.array);
		if (uv) uvs.push(...uv.array);
		if (geometry !== source) geometry.dispose();
	}
	const merged = new BufferGeometry();
	merged.setAttribute('position', new Float32BufferAttribute(positions, 3));
	if (normals.length) merged.setAttribute('normal', new Float32BufferAttribute(normals, 3));
	if (uvs.length) merged.setAttribute('uv', new Float32BufferAttribute(uvs, 2));
	merged.computeBoundingSphere();
	return merged;
}

const buildingTexture1 = textureLoader.load('/assets/gfx/textures/building-1.jpg');

function createBoxMesh(color, w, l, h, x = 0, y = 0, z = 0, shadow = true) {
	const material = color === 'wall'
		? [
			new MeshBasicMaterial({ map: buildingTexture1 }),
			new MeshBasicMaterial({ map: buildingTexture1 }),
			new MeshBasicMaterial({ map: buildingTexture1 }),
			new MeshBasicMaterial({ map: buildingTexture1 }),
			new MeshBasicMaterial({ color: 0x333333 }),
			new MeshBasicMaterial({ color: 0x333333 }),
		]
		: new MeshBasicMaterial({ color });
	const mesh = new Mesh(new BoxGeometry(w, l, h), material);
	mesh.position.set(x, y, z);
	mesh.receiveShadow = true;
	if (shadow) mesh.castShadow = true;
	return mesh;
}

function createBoxMeshWithOptions(options = {}) {
	return createBoxMesh(options.color, options.w, options.l, options.h, options.x, options.y, options.z, options.shadow);
}

function chance(percent) {
	return Math.random() < percent / 100.0;
}

function getRandInt(min, max, exp) {
	return mapToRange(Math.random(), min, max, exp);
}

function isPointInCircle(center, radius, point) {
	const dx = point.x - center.x;
	const dy = point.y - center.y;
	return dx * dx + dy * dy <= radius * radius;
}
